import { ModalHeaderExtrasProvider } from "@msys/ui";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RestrictedByCapability } from "../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermission } from "../auth/RestrictedByOrganisationPermission.js";
import { MainMenu } from "../commons/MainMenu.js";
import { PageNotFound } from "../commons/PageNotFound.js";
import { AgentAccessProvider } from "../features/agent/AgentAccessProvider.js";
import { AgentAssistButton } from "../features/agent/AgentAssistButton.js";
import { AgentDialogFormValuesProvider } from "../features/agent/agentDialogFormValues.js";
import { AgentPanel } from "../features/agent/AgentPanel.js";
import { BuildingsRoutes } from "./buildings/BuildingsRoutes.js";
import { Calendar } from "./calendar/Calendar.js";
import { CrmRoutes } from "./crm/CrmRoutes.js";
import { Dashboard } from "./dashboard/Dashboard.js";
import { DocumentsRoutes } from "./documents/DocumentsRoutes.js";
import { ExamplesRoutes } from "./examples/ExamplesRoutes.js";
import { OrganisationRoutes } from "./my-organisation/OrganisationRoutes.js";
import { UserRoutes } from "./my-user/UserRoutes.js";
import { OpportunitiesRoutes } from "./opportunities/OpportunitiesRoutes.js";
import { PlanningRoutes } from "./planning/PlanningRoutes.js";
import { ProductsRoutes } from "./products/ProductsRoutes.js";
import { ProjectsRoutes } from "./projects/ProjectsRoutes.js";
import { Schedule } from "./schedule/Schedule.js";
import { TasksRoutes } from "./tasks/TaskRoutes.js";
import { TemplatesRoutes } from "./templates/TemplatesRoutes.js";
import { CallAssistant } from "./call-assistant/CallAssistant.js";
import { useMainMenuItems } from "./useMainMenuItems.js";
import { WebShopCms } from "./webshopcms/WebShopCms.js";

const DASHBOARD_PATH = "/dashboard";

export const MainRoutes = () => {
  const location = useLocation();
  const { primaryItems, secondaryItems } = useMainMenuItems();

  return (
    <AgentAccessProvider>
      {/* Every @msys/ui `Modal` gets the "AI suggestions" button in its
          header for free, following the same visibility rule as the panel
          (`useAgentAvailable`) — `AgentAssistButton` renders `null` itself
          when unavailable, so this is unconditional. */}
      <AgentDialogFormValuesProvider>
        <ModalHeaderExtrasProvider extras={<AgentAssistButton kind="dialog" />}>
          <MainMenu
            primaryItems={primaryItems}
            secondaryItems={secondaryItems}
          />
          <AgentPanel />
          <Routes>
            <Route
              path={"/"}
              element={
                <Navigate
                  to={{
                    pathname: DASHBOARD_PATH,
                    search: location.search,
                  }}
                  replace
                />
              }
            />
            <Route
              path={"/next"}
              element={
                <Navigate
                  to={{
                    pathname: DASHBOARD_PATH,
                    search: location.search,
                  }}
                  replace
                />
              }
            />

            <Route path={DASHBOARD_PATH} element={<Dashboard />} />
            <Route path={"/buildings/*"} element={<BuildingsRoutes />} />
            <Route
              path={`/opportunities/*`}
              element={
                <RestrictedByOrganisationPermission permission="organisation_projects_create">
                  <OpportunitiesRoutes />
                </RestrictedByOrganisationPermission>
              }
            />
            <Route path={`/projects/*`} element={<ProjectsRoutes />} />
            <Route path={`/documents/*`} element={<DocumentsRoutes />} />
            <Route path={`/tasks/*`} element={<TasksRoutes />} />
            <Route
              path={`/planning/*`}
              element={
                <RestrictedByCapability capability="planning">
                  <PlanningRoutes />
                </RestrictedByCapability>
              }
            />
            <Route
              path={"/crm/*"}
              element={
                <RestrictedByOrganisationPermission permission="crm_record_read">
                  <CrmRoutes />
                </RestrictedByOrganisationPermission>
              }
            />
            <Route
              path={`/templates/*`}
              element={
                <RestrictedByCapability capability="templating">
                  <RestrictedByOrganisationPermission permission="template_document_read">
                    <TemplatesRoutes />
                  </RestrictedByOrganisationPermission>
                </RestrictedByCapability>
              }
            />
            <Route
              path={"/products/*"}
              element={
                <RestrictedByCapability capability="pim">
                  <ProductsRoutes />
                </RestrictedByCapability>
              }
            />

            <Route
              path={"/callassistant/*"}
              element={
                <RestrictedByCapability capability="callAssistant">
                  <CallAssistant />
                </RestrictedByCapability>
              }
            />
            <Route
              path={"/webshopcms/*"}
              element={
                <RestrictedByCapability capability="webshopcms">
                  <WebShopCms />
                </RestrictedByCapability>
              }
            />

            <Route path={"/organisation/*"} element={<OrganisationRoutes />} />
            <Route path={"/user/*"} element={<UserRoutes />} />

            <Route path={`/schedule`} element={<Schedule />} />
            <Route path={`/calendar`} element={<Calendar />} />

            <Route path={`/examples/*`} element={<ExamplesRoutes />} />

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </ModalHeaderExtrasProvider>
      </AgentDialogFormValuesProvider>
    </AgentAccessProvider>
  );
};
